import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Checkbox,
  ChevronLeftIcon,
  XIcon,
  CodeXmlIcon,
  Flex,
  Form,
  Heading,
  IconButton,
  Input,
  KeyRoundIcon,
  Link,
  Notice,
  PlusIcon,
  Text,
  TrashIcon,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import {
  focusFirstInvalidControl,
  getFirstErrorMessage,
  revalidateLogic,
  usePopcornForm,
} from '@buffer-mono/popcorn/forms'
import { UploadType } from '@buffer-mono/uploader'

import { NewSettingsPageLayout } from '../../../shared/components/NewSettingsPageLayout'
import { PhotoUploader } from '../../../shared/components/PhotoUploader'
import type { APIClient, RegisterAPIClientInput } from '../../hooks'
import { useOAuthScopes, useValidateAPIClientName } from '../../hooks'
import { CredentialsDialog } from '../CredentialsDialog'
import { UsagePanel } from '../UsagePanel'

import {
  apiClientNameSchema,
  createApiClientFormSchema,
  MAX_REDIRECT_URLS,
} from './ApiClientForm.schema'
import type { ApiClientFormValues } from './ApiClientForm.schema'
import styles from './ApiClientForm.module.css'

type SubmitResult = { success: true } | { success: false; error: string }

const TERMS_URL = 'https://buffer.com/terms'

const isReservedNameError = (error: unknown): boolean =>
  typeof error === 'string' && error.includes('It is reserved')

const NAME_VALIDATION_DEBOUNCE_MS = 300

interface ApiClientFormProps {
  client: APIClient | null
  onSubmit: (input: RegisterAPIClientInput) => Promise<SubmitResult>
  isSubmitting: boolean
  onBack: () => void
  onSuccess: () => void
  onDelete?: () => void
}

const createRedirectUrl = (
  value: string,
): ApiClientFormValues['redirectUrls'][number] => ({
  id: window.crypto.randomUUID(),
  value,
})

const createDefaultValues = (
  client: APIClient | null,
): ApiClientFormValues => ({
  clientName: client?.clientName ?? '',
  privacyPolicyUrl: client?.privacyPolicyUri ?? '',
  logoUri: client?.logoUri ?? null,
  isPublicClient: false,
  redirectUrls: client?.redirectUris?.length
    ? client.redirectUris.map(createRedirectUrl)
    : [createRedirectUrl('')],
  scopeNames:
    client?.scopes?.flatMap((scope) => (scope.name ? [scope.name] : [])) ?? [],
})

const getSubmittedRedirectUrls = (values: ApiClientFormValues): string[] =>
  values.redirectUrls
    .map(({ value }) => value)
    .filter((value) => value.trim() !== '')

const haveSameStringValues = (first: string[], second: string[]): boolean =>
  first.length === second.length &&
  first.every((value, index) => value === second[index])

const haveSameStringSet = (first: string[], second: string[]): boolean =>
  first.length === second.length &&
  first.every((value) => second.includes(value))

const haveSameEditableValues = (
  first: ApiClientFormValues,
  second: ApiClientFormValues,
): boolean =>
  first.clientName === second.clientName &&
  first.privacyPolicyUrl === second.privacyPolicyUrl &&
  first.logoUri === second.logoUri &&
  haveSameStringValues(
    getSubmittedRedirectUrls(first),
    getSubmittedRedirectUrls(second),
  ) &&
  haveSameStringSet(first.scopeNames, second.scopeNames)

const createSubmitInput = (
  values: ApiClientFormValues,
  client: APIClient | null,
): RegisterAPIClientInput => {
  const privacyPolicyUri = values.privacyPolicyUrl.trim()
  const logoChanged = values.logoUri !== (client?.logoUri ?? null)

  return {
    clientName: values.clientName.trim(),
    redirectUris: getSubmittedRedirectUrls(values),
    scopes: values.scopeNames.length > 0 ? values.scopeNames : undefined,
    ...(privacyPolicyUri && { privacyPolicyUri }),
    ...(logoChanged && { logoUri: values.logoUri }),
    ...(!client && values.isPublicClient && { isPublicClient: true }),
  }
}

export function ApiClientForm({
  client,
  onSubmit,
  isSubmitting,
  onBack,
  onSuccess,
  onDelete,
}: ApiClientFormProps): JSX.Element {
  const isEditing = client !== null
  const clientSecret = client?.clientSecret
  const clientHasPrivacyPolicy = isEditing && !!client.privacyPolicyUri
  const formRef = useRef<HTMLFormElement>(null)
  const defaultValuesRef = useRef<ApiClientFormValues | null>(null)
  const nameValidationTimeoutRef = useRef<number | null>(null)
  const nameValidationAbortControllerRef = useRef<AbortController | null>(null)
  const nameValidationVersionRef = useRef(0)
  const hasSeededScopes = useRef(isEditing)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false)

  if (defaultValuesRef.current === null) {
    defaultValuesRef.current = createDefaultValues(client)
  }

  const defaultValues = defaultValuesRef.current
  const grantedScopeNames = new Set(defaultValues.scopeNames)
  const { scopes } = useOAuthScopes()
  const { validate: validateNameOnServer } = useValidateAPIClientName()

  const cancelNameValidation = (): void => {
    if (nameValidationTimeoutRef.current !== null) {
      window.clearTimeout(nameValidationTimeoutRef.current)
      nameValidationTimeoutRef.current = null
    }

    nameValidationAbortControllerRef.current?.abort()
    nameValidationAbortControllerRef.current = null
    nameValidationVersionRef.current += 1
  }

  const form = usePopcornForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: createApiClientFormSchema(clientHasPrivacyPolicy),
      onSubmitAsync: async ({ formApi, signal, value }) => {
        if (!apiClientNameSchema.safeParse(value.clientName).success) {
          return undefined
        }

        cancelNameValidation()
        const error = await validateNameOnServer(
          value.clientName.trim(),
          signal,
        )

        if (
          signal.aborted ||
          formApi.state.values.clientName !== value.clientName
        ) {
          return undefined
        }

        return error
          ? {
              fields: {
                clientName: error,
              },
            }
          : undefined
      },
    },
    listeners: {
      onChange: ({ fieldApi }) => {
        setSubmitError(null)

        if (fieldApi.name === 'clientName') {
          cancelNameValidation()
          fieldApi.setErrorMap({
            onBlur: undefined,
            onSubmit: undefined,
          })
        }
      },
      onBlur: ({ fieldApi, formApi }) => {
        if (fieldApi.name !== 'clientName') return

        const value = formApi.state.values.clientName
        if (!apiClientNameSchema.safeParse(value).success) return

        cancelNameValidation()
        const validationVersion = nameValidationVersionRef.current

        nameValidationTimeoutRef.current = window.setTimeout(() => {
          nameValidationTimeoutRef.current = null

          if (
            formApi.state.isSubmitting ||
            formApi.state.values.clientName !== value
          ) {
            return
          }

          const abortController = new AbortController()
          nameValidationAbortControllerRef.current = abortController

          void validateNameOnServer(value.trim(), abortController.signal)
            .then((error) => {
              if (
                abortController.signal.aborted ||
                validationVersion !== nameValidationVersionRef.current ||
                formApi.state.values.clientName !== value
              ) {
                return undefined
              }

              fieldApi.setErrorMap({ onBlur: error ?? undefined })
              return undefined
            })
            .finally(() => {
              if (
                nameValidationAbortControllerRef.current === abortController
              ) {
                nameValidationAbortControllerRef.current = null
              }
            })
        }, NAME_VALIDATION_DEBOUNCE_MS)
      },
    },
    onSubmitInvalid: () => focusFirstInvalidControl(formRef.current),
    onSubmit: async ({ value }) => {
      setSubmitError(null)
      const result = await onSubmit(createSubmitInput(value, client))

      if (result.success) {
        onSuccess()
      } else {
        setSubmitError(result.error)
      }
    },
  })

  useEffect(() => {
    if (hasSeededScopes.current || scopes.length === 0) return

    form.setFieldValue(
      'scopeNames',
      scopes.map((scope) => scope.name),
      {
        dontRunListeners: true,
        dontUpdateMeta: true,
        dontValidate: true,
      },
    )
    hasSeededScopes.current = true
  }, [form, scopes])

  useEffect(
    () => (): void => {
      if (nameValidationTimeoutRef.current !== null) {
        window.clearTimeout(nameValidationTimeoutRef.current)
      }
      nameValidationAbortControllerRef.current?.abort()
      nameValidationVersionRef.current += 1
    },
    [],
  )

  const pageTitle = isEditing ? client.clientName : 'New Client'

  const titleWithBack = (
    <Flex direction="column" gap="md" align="start">
      <UnstyledButton onClick={onBack} className={styles.backLink}>
        <Flex align="center" gap="3xs">
          <ChevronLeftIcon size={16} />
          <Text size="sm">Buffer API</Text>
        </Flex>
      </UnstyledButton>

      {isEditing ? (
        <div className={styles.titleRow}>
          {client.logoUri ? (
            <img
              src={client.logoUri}
              alt={`${client.clientName} logo`}
              className={styles.clientLogo}
            />
          ) : (
            <div className={styles.iconBadge}>
              <CodeXmlIcon />
            </div>
          )}
          {client.clientName}
        </div>
      ) : (
        'New Client'
      )}
    </Flex>
  )

  const actions = isEditing ? (
    <Flex gap="xs">
      <Button
        variant="secondary"
        onClick={(): void => setIsCredentialsOpen(true)}
      >
        <KeyRoundIcon />
        Credentials
      </Button>

      {onDelete && (
        <Button variant="secondary" onClick={onDelete}>
          <TrashIcon />
          Delete
        </Button>
      )}
    </Flex>
  ) : undefined

  return (
    <NewSettingsPageLayout
      title={titleWithBack}
      pageTitle={pageTitle}
      actions={actions}
      gap="xs"
    >
      <Form
        ref={formRef}
        name="apiClientForm"
        className={styles.form}
        noValidate
        onSubmit={(): void => {
          cancelNameValidation()
          void form.handleSubmit()
        }}
      >
        {submitError && <Notice variant="error">{submitError}</Notice>}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isFormSubmitting) => {
            const submitInProgress = isFormSubmitting || isSubmitting

            return (
              <fieldset
                className={styles.fieldset}
                disabled={submitInProgress}
                aria-busy={submitInProgress}
              >
                {isEditing && (
                  <div className={styles.section}>
                    <UsagePanel
                      clientId={client.clientId}
                      tooltipText="Usage is reset on a rolling basis and apply to this app client only."
                    />
                  </div>
                )}

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>
                    <Heading as="h2" size="sm">
                      Info
                    </Heading>

                    <Text color="subtle">
                      Provide a client name and optional logo shown to users
                      during authentication
                    </Text>
                  </div>

                  <div className={styles.fieldGroup}>
                    <form.AppField name="clientName">
                      {(field) => {
                        const error = getFirstErrorMessage(
                          field.state.meta.errors,
                        )
                        const showTermsLink =
                          isReservedNameError(
                            field.state.meta.errorMap.onBlur,
                          ) ||
                          isReservedNameError(
                            field.state.meta.errorMap.onSubmit,
                          )

                        return (
                          <Form.Field
                            className={styles.field}
                            name={field.name}
                            invalid={!field.state.meta.isValid}
                            required
                          >
                            <Form.Label>
                              Name <Form.RequiredIndicator />
                            </Form.Label>
                            <Form.Control>
                              <Input
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(event) =>
                                  field.handleChange(event.currentTarget.value)
                                }
                                placeholder="My App"
                                autoComplete="off"
                                required
                              />
                            </Form.Control>
                            {error ? (
                              <Form.Error
                                role={
                                  field.state.meta.errorMap.onBlur
                                    ? 'alert'
                                    : undefined
                                }
                              >
                                {error}
                                {showTermsLink && (
                                  <>
                                    {' '}
                                    <Link href={TERMS_URL} external>
                                      More info
                                    </Link>
                                  </>
                                )}
                              </Form.Error>
                            ) : (
                              <Form.Description>
                                Use at least 2 characters. Include a letter;
                                numbers and spaces are allowed.
                              </Form.Description>
                            )}
                          </Form.Field>
                        )
                      }}
                    </form.AppField>

                    <form.Field name="logoUri">
                      {(field) => (
                        <PhotoUploader
                          src={field.state.value ?? undefined}
                          alt="Client logo"
                          label="Logo"
                          description="Use an image at least 180x180 pixels in size."
                          uploadType={UploadType.clientLogo}
                          cropSize={200}
                          onUploadComplete={field.handleChange}
                          onRemove={(): void => field.handleChange(null)}
                          onLoadingChange={setIsUploading}
                          uploadErrorMessage="Logo upload failed. Please try again."
                        />
                      )}
                    </form.Field>
                  </div>

                  <form.AppField name="privacyPolicyUrl">
                    {(field) => (
                      <field.TextField
                        label="Privacy Policy URL"
                        description="Ensure your privacy policy is publicly accessible"
                        fieldClassName={styles.field}
                        placeholder="eg. https://example.com/privacy"
                        inputMode="url"
                        type="url"
                        required={clientHasPrivacyPolicy}
                      />
                    )}
                  </form.AppField>

                  {!isEditing && (
                    <form.AppField name="isPublicClient">
                      {(field) => (
                        <field.CheckboxField
                          fieldClassName={styles.publicClientCheckbox}
                          label="Public client (no client secret) — cannot be changed later"
                          description="Enable this for apps where the source code is publicly accessible, such as open-source projects, browser extensions, or mobile apps. Public clients use PKCE for security instead of a client secret. This choice is permanent and cannot be changed after the client is created."
                        />
                      )}
                    </form.AppField>
                  )}
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>
                    <Heading as="h2" size="sm">
                      Permissions
                    </Heading>

                    <Text color="subtle">
                      Select the permissions this app can request. Granted
                      permissions can&apos;t be removed.
                    </Text>
                  </div>

                  <form.Field name="scopeNames">
                    {(field) => (
                      <div className={styles.scopesList}>
                        {scopes.map((scope) => {
                          const isGranted = grantedScopeNames.has(scope.name)
                          const isChecked = field.state.value.includes(
                            scope.name,
                          )

                          return (
                            <Checkbox
                              key={scope.name}
                              checked={isChecked}
                              disabled={isGranted}
                              className={
                                isGranted ? styles.grantedCheckbox : undefined
                              }
                              onChange={(checked): void => {
                                if (checked === true && !isChecked) {
                                  field.handleChange([
                                    ...field.state.value,
                                    scope.name,
                                  ])
                                } else if (checked !== true && isChecked) {
                                  field.handleChange(
                                    field.state.value.filter(
                                      (name) => name !== scope.name,
                                    ),
                                  )
                                }
                              }}
                            >
                              <Text size="sm">
                                <Text as="span" weight="medium" size="sm">
                                  {scope.name}:
                                </Text>{' '}
                                <Text as="span" color="subtle" size="sm">
                                  {scope.description}
                                </Text>
                                {isGranted && (
                                  <Text as="span" color="subtle" size="sm">
                                    {' '}
                                    · Granted
                                  </Text>
                                )}
                              </Text>
                            </Checkbox>
                          )
                        })}
                      </div>
                    )}
                  </form.Field>
                </div>

                <div className={styles.sectionNoBorder}>
                  <div className={styles.sectionTitle}>
                    <Heading as="h2" size="sm">
                      Redirects
                    </Heading>

                    <Text color="subtle">
                      Add one to five complete HTTPS URLs to redirect to after
                      users authenticate with Buffer. At least one is required.
                    </Text>
                  </div>

                  <form.AppField name="redirectUrls" mode="array">
                    {(redirectUrlsField) => (
                      <Flex direction="column" gap="xs" fullWidth>
                        {redirectUrlsField.state.value.map(
                          (redirectUrl, index) => (
                            <Flex
                              key={redirectUrl.id}
                              direction="column"
                              gap="2xs"
                              fullWidth
                            >
                              <Flex align="center" gap="xs" fullWidth>
                                <form.AppField
                                  name={`redirectUrls[${index}].value`}
                                >
                                  {(field) => (
                                    <field.TextField
                                      label={
                                        <VisuallyHidden>
                                          Redirect URL {index + 1}
                                        </VisuallyHidden>
                                      }
                                      fieldClassName={styles.redirectUrlInput}
                                      placeholder="https://example.com/callback"
                                      inputMode="url"
                                      type="url"
                                    />
                                  )}
                                </form.AppField>

                                {redirectUrlsField.state.value.length > 1 && (
                                  <IconButton
                                    type="button"
                                    variant="tertiary"
                                    label={`Remove redirect URL ${index + 1}`}
                                    onClick={(): void =>
                                      redirectUrlsField.removeValue(index)
                                    }
                                  >
                                    <XIcon />
                                  </IconButton>
                                )}
                              </Flex>
                            </Flex>
                          ),
                        )}

                        {redirectUrlsField.state.value.length <
                          MAX_REDIRECT_URLS && (
                          <Button
                            type="button"
                            variant="tertiary"
                            onClick={(): void =>
                              redirectUrlsField.pushValue(createRedirectUrl(''))
                            }
                          >
                            <PlusIcon />
                            Add Another URL
                          </Button>
                        )}
                      </Flex>
                    )}
                  </form.AppField>

                  <div className={styles.sectionFooter}>
                    <form.Subscribe selector={(state) => state.values}>
                      {(values) => {
                        const hasChanges = !haveSameEditableValues(
                          defaultValues,
                          values,
                        )
                        const submitDisabled =
                          (isEditing && !hasChanges) ||
                          isUploading ||
                          submitInProgress

                        return (
                          <Button
                            type="submit"
                            variant="primary"
                            loading={submitInProgress}
                            disabled={submitDisabled}
                          >
                            {isEditing ? 'Save' : 'Create Client'}
                          </Button>
                        )
                      }}
                    </form.Subscribe>
                  </div>
                </div>
              </fieldset>
            )
          }}
        </form.Subscribe>
      </Form>

      {isEditing && (
        <CredentialsDialog
          open={isCredentialsOpen}
          onOpenChange={setIsCredentialsOpen}
          clientId={client.clientId}
          clientSecret={clientSecret}
        />
      )}
    </NewSettingsPageLayout>
  )
}
